import { toDate } from "@flaminem/utils";

import {
  CaseFragmentFragment,
  CaseAlertStatus as ApiCaseAlertStatus,
  BlacklistedIdentifierFragmentFragment,
  ShortCaseElementFragmentFragment,
  CaseSummaryFragmentFragment,
  BlacklistableIdentifierFragmentFragment,
  CaseType,
  LinkedCaseFragmentFragment,
} from "src/graphql/anti-fraud/__generated__/graphql";
import { getPropertyValueByCode } from "src/services/property/getPropertyByCode";
import {
  BlacklistableCaseIdentifier,
  BlacklistedCaseIdentifier,
  Case,
  CaseAlertStatus,
  CaseSummary,
  LinkedCase,
} from "src/types/case";
import { ShortElement } from "src/types/element";
import { PropertyValue } from "src/types/property";
import { hasType } from "src/types/typedValue";

import { mapToAdminStatus } from "./adminStatus.mappers";
import { mapToShortAlert } from "./alert.mappers";
import { mapToElement } from "./element.mappers";
import { mapIdentifier } from "./identifier.mapper";
import { mapToPropertyValue } from "./property.mappers";
import { mapToScenarioExecutionResultWithCase } from "./scenario.mappers";
import { mapBusinessTypeToAppDataType, parseTypedValue } from "./typedValue.mappers";

export const mapToCase = (fragment: CaseFragmentFragment): Case => {
  const properties = fragment.properties.map(mapToPropertyValue);
  const requestProperty = getPropertyValueByCode(properties, "id");
  const request =
    requestProperty && hasType(requestProperty.value, "string")
      ? requestProperty.value.value
      : null;

  return {
    id: fragment.id,
    request,
    alerts: fragment.alerts.map((alert) => mapToShortAlert(alert)),
    executionResults: fragment.executionResults.map((executionResult) =>
      mapToScenarioExecutionResultWithCase(executionResult)
    ),
    snapshotCreation: fragment.snapshotCreation ? toDate(fragment.snapshotCreation) : null,
    typeCode: fragment.typeCode,
    lastScore: fragment.lastScore,
    configurableType: {
      code: fragment.configurableType.code,
      name: fragment.configurableType.name,
    },
    alertStatus: mapToCaseAlertStatus(fragment.alertStatus),
    properties,
    elements: fragment.elements.map(mapToElement),
    blacklistedIdentifiers: fragment.blacklistedIdentifiers
      ? fragment.blacklistedIdentifiers.map(mapToBlacklistedCaseIdentifier)
      : [],
    adminStatus: mapToAdminStatus(fragment.adminStatus),
    elementLists: (fragment.elementLists ?? []).map((elementList) => ({
      code: elementList.code,
      name: elementList.name,
      typeCode: elementList.typeCode,
      elements: elementList.elements.map((element) => ({
        id: element.id,
        name: element.name,
        properties: element.properties.map(mapToPropertyValue),
      })),
    })),
  };
};

export const mapToLinkedCase = (fragment: LinkedCaseFragmentFragment): LinkedCase => {
  const mapLightPropertyValue = (
    pv: LinkedCaseFragmentFragment["properties"][number]
  ): PropertyValue => ({
    value: parseTypedValue(pv.value, mapBusinessTypeToAppDataType(pv.property.businessType)),
    property: {
      code: pv.property.code,
      name: "",
      dataType: mapBusinessTypeToAppDataType(pv.property.businessType),
      mandatory: false,
      constraints: [],
      confListCode: pv.property.confListCode,
    },
  });

  return {
    id: fragment.id,
    snapshotCreation: fragment.snapshotCreation ? toDate(fragment.snapshotCreation) : null,
    typeCode: fragment.typeCode,
    lastScore: fragment.lastScore,
    configurableType: {
      code: fragment.configurableType.code,
      name: fragment.configurableType.name,
    },
    alertStatus: mapToCaseAlertStatus(fragment.alertStatus),
    properties: fragment.properties.map(mapLightPropertyValue),
    elements: fragment.elements.map((element) => ({
      id: element.id,
      name: element.name,
      typeCode: element.typeCode,
      configurableType: {
        code: element.configurableType.code,
        name: element.configurableType.name,
      },
      schemaElement: {
        code: element.schemaElement.code,
        name: element.schemaElement.name,
      },
      identifiers: [],
      properties: element.properties.map(mapLightPropertyValue),
      elements: element.elements.map((nested) => ({
        id: nested.id,
        name: nested.name,
        typeCode: nested.typeCode,
        configurableType: {
          code: nested.configurableType.code,
          name: nested.configurableType.name,
        },
        schemaElement: nested.schemaElement
          ? { code: nested.schemaElement.code, name: nested.schemaElement.name }
          : undefined,
        properties: nested.properties.map(mapLightPropertyValue),
      })),
    })),
  };
};

export const mapToBlacklistableCaseIdentifier = (
  blacklistableCaseIdentifier: BlacklistableIdentifierFragmentFragment
): BlacklistableCaseIdentifier => {
  return {
    id: `${blacklistableCaseIdentifier.identifier.id}-${blacklistableCaseIdentifier.element.id}-${blacklistableCaseIdentifier.blacklist.code}`,
    element: {
      id: blacklistableCaseIdentifier.element.id,
      name: blacklistableCaseIdentifier.element.name,
      typeCode: blacklistableCaseIdentifier.element.typeCode,
    },
    identifier: {
      id: blacklistableCaseIdentifier.identifier.id,
      value: blacklistableCaseIdentifier.identifier.value,
      schemaIdentifier: {
        code: blacklistableCaseIdentifier.identifier.schemaIdentifier.code,
      },
      identifierType: {
        name: blacklistableCaseIdentifier.identifier.identifierType.name,
        code: blacklistableCaseIdentifier.identifier.identifierType.code,
      },
    },
    belongsToCurrentCase: blacklistableCaseIdentifier.belongsToCurrentCase,
    linkedCases: blacklistableCaseIdentifier.linkedCases.map((linkedCase) => ({
      id: linkedCase.id,
    })),
    blacklist: {
      name: blacklistableCaseIdentifier.blacklist.name,
      code: blacklistableCaseIdentifier.blacklist.code,
    },
  };
};

export const mapToShortCaseElement = (
  caseElement: ShortCaseElementFragmentFragment
): ShortElement => {
  return {
    id: caseElement.id,
    configurableType: {
      code: caseElement.configurableType.code,
      name: caseElement.configurableType.name,
    },
    properties: caseElement.properties.map(mapToPropertyValue),
  };
};

export const mapToCaseSummary = (fragment: CaseSummaryFragmentFragment): CaseSummary => {
  const properties = fragment.properties.map(mapToPropertyValue);

  const requestProperty = getPropertyValueByCode(properties, "id");
  const request =
    requestProperty && hasType(requestProperty.value, "string")
      ? requestProperty.value.value
      : null;

  return {
    id: fragment.id,
    lastScore: fragment.lastScore,
    request,
    properties,
    elements: fragment.elements.map(mapToElement),
    snapshotCreation: fragment.snapshotCreation ? toDate(fragment.snapshotCreation) : null,
    adminStatus: mapToAdminStatus(fragment.adminStatus),
    typeCode: fragment.typeCode,
  };
};

const CASE_STATUS_MAP: Record<ApiCaseAlertStatus, CaseAlertStatus> = {
  COMPLIANT: "compliant",
  FRAUD_PREVENTED: "fraudPrevented",
  INITIAL: "initial",
  PENDING_DECISION: "pendingDecision",
  PROVEN_FRAUD_WITH_FINANCIAL_LOSS: "provenFraudWithFinancialLoss",
  PROVEN_FRAUD_WITH_NO_FINANCIAL_LOSS: "provenFraudWithNoFinancialLoss",
  SCORED: "scored",
};

export const mapToCaseAlertStatus = (status: ApiCaseAlertStatus): CaseAlertStatus => {
  return CASE_STATUS_MAP[status];
};

export const mapToBlacklistedCaseIdentifier = (
  fragment: BlacklistedIdentifierFragmentFragment
): BlacklistedCaseIdentifier => ({
  element: {
    name: fragment.element.name,
    typeCode: fragment.element.typeCode,
  },
  identifier: mapIdentifier(fragment.identifier),
  blacklistElement: {
    blacklist: {
      name: fragment.blacklistElement.blacklist.name,
      code: fragment.blacklistElement.blacklist.code,
    },
  },
});

export const mapToCaseTabs = (
  caseType: Pick<CaseType, "code" | "name">
): { code: string; label: string } => ({
  code: caseType.code,
  label: caseType.name,
});
